import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Webhook, CreditCard, Mail, AlertCircle } from 'lucide-react';

export default function AdminWebhookLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadLogs = (eventType) => {
    setLoading(true);
    const token = localStorage.getItem('funnely_token');
    const params = new URLSearchParams({ limit: '100' });
    if (eventType && eventType !== 'all') params.set('event_type', eventType);
    
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/webhook-logs?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setLogs(d.logs || []); setTotal(d.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { loadLogs(filter); }, [filter]);

  const getIcon = (type) => {
    if (type?.includes('stripe') || type?.includes('invoice') || type?.includes('payment')) return <CreditCard className="h-3.5 w-3.5 text-[#635BFF]" />;
    if (type?.includes('email') || type?.includes('resend')) return <Mail className="h-3.5 w-3.5 text-[#FF9A00]" />;
    return <Webhook className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const getStatusColor = (status) => {
    if (status === 'success' || status === 'delivered') return 'bg-emerald-100 text-emerald-700';
    if (status === 'failed' || status === 'bounced') return 'bg-red-100 text-red-700';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6" data-testid="admin-webhook-logs">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>Webhook Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">{total} total events</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="resend">Resend</SelectItem>
            <SelectItem value="payment">Payment</SelectItem>
            <SelectItem value="subscription">Subscription</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-12 bg-muted animate-pulse rounded" />)}</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No webhook events recorded yet</p>
              <p className="text-xs text-muted-foreground mt-1">Events will appear here when Stripe or Resend sends webhooks</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[500px]">
              <div className="space-y-1">
                {logs.map((log, i) => (
                  <div key={log.id || i} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-muted/50 border-b border-border/50 last:border-0">
                    {getIcon(log.type)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{log.type || 'unknown'}</p>
                      <p className="text-xs text-muted-foreground truncate">{log.description || log.email || ''}</p>
                    </div>
                    <Badge className={`text-[10px] ${getStatusColor(log.status)}`}>{log.status || 'received'}</Badge>
                    <span className="text-[10px] text-muted-foreground shrink-0">{log.created_at ? new Date(log.created_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
